import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Users, Loader2, Phone, CalendarClock, User as UserIcon, Crown } from 'lucide-react';
import RightDrawer from './RightDrawer';
import PatientCard from './PatientCard';
import { useTenant } from '../contexts/TenantContext';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

/**
 * Family drawer — renders the patient's family group with the primary
 * patient called out and every member shown as a drillable PatientCard.
 * Designed to be opened from the reception queue, doctor's dashboard
 * and the inline patient search card.
 */
export default function PatientFamilyDrawer({ open, onClose, patient, onPick }) {
  const [family, setFamily] = useState({ primary_patient_id: null, members: [] });
  const [loading, setLoading] = useState(false);
  const { tenant } = useTenant();
  const brand = tenant?.branding?.primary_color || '#0f766e';

  useEffect(() => {
    if (!open || !patient?.id) return;
    setLoading(true);
    ax.get(`/api/clinic-ops/patients/${patient.id}/family`)
      .then(({ data }) => setFamily(data || { primary_patient_id: null, members: [] }))
      .catch(() => setFamily({ primary_patient_id: null, members: [] }))
      .finally(() => setLoading(false));
  }, [open, patient?.id]);

  const members = family.members || [];
  const primary = members.find(m => m.id === family.primary_patient_id) || members[0];
  const others = members.filter(m => m.id !== primary?.id);

  return (
    <RightDrawer
      open={open}
      onClose={onClose}
      title={patient?.name ? `${patient.name}'s family` : 'Family group'}
      subtitle={patient?.phone ? `Phone · ${patient.phone}` : 'Patient family chart'}
      icon={<Users className="h-5 w-5 text-white" />}
      accent={brand}
      data-testid="family-drawer"
    >
      <div className="p-6 space-y-5">
        {loading ? (
          <div className="py-16 text-center text-zinc-400">
            <Loader2 className="h-6 w-6 mx-auto animate-spin" />
            <p className="text-sm mt-2">Loading family chart…</p>
          </div>
        ) : members.length === 0 ? (
          <div className="py-16 text-center text-zinc-400">
            <Users className="h-8 w-8 mx-auto opacity-40" />
            <p className="text-sm mt-2">No family members linked yet.</p>
          </div>
        ) : (
          <>
            <Stat
              icon={<UserIcon className="h-4 w-4" />}
              label="Members on this phone"
              value={members.length}
              brand={brand}
            />

            {primary && (
              <section>
                <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-2 flex items-center gap-1.5">
                  <Crown className="h-3 w-3 text-amber-500" /> Primary patient
                </div>
                <PatientCard patient={primary} onOpen={() => onPick?.(primary)} />
              </section>
            )}

            {others.length > 0 && (
              <section>
                <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-2">
                  Family members ({others.length})
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {others.map(m => (
                    <PatientCard key={m.id} patient={m} onOpen={() => onPick?.(m)} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </RightDrawer>
  );
}

function Stat({ icon, label, value, brand }) {
  return (
    <div className="rounded-xl border bg-white p-3 flex items-center gap-3" style={{ borderColor: `${brand}33` }}>
      <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white"
           style={{ background: `linear-gradient(135deg, ${brand}, ${brand}cc)` }}>
        {icon}
      </div>
      <div>
        <div className="text-xs text-zinc-500">{label}</div>
        <div className="text-xl font-extrabold text-zinc-900" style={{ fontFamily: 'Manrope' }}>{value}</div>
      </div>
    </div>
  );
}
